// Keep this method so that d3-drag will work, otherwise need to get d3-force simulation to work with React independently.

import { forceSimulation, forceManyBody, forceLink, forceCenter, forceRadial, forceCollide, forceY } from 'd3-force';

import { keyBy, flatten } from 'lodash';
import { select, event } from 'd3-selection';
import { makeTree, getBuildLinePath, buildCrosslink, buildLinePathStraight } from '../../utils/tree';
import { isUndefined } from 'lodash';
import { drag } from 'd3-drag';


const getCrossLinks = (nodeList) => {
  const nodeMap = keyBy(nodeList, "data.name");
  const makeCrossLinks = d => {
    const neighbors = d.data.crosslinks;
    const start = d;
    return neighbors.split("|").map(neighbor => {
      const end = nodeMap[neighbor];
      return [start, end];
    });
  };

  const nodesWithCrossLink = nodeList.filter(d => d.data.crosslinks);
  return flatten(nodesWithCrossLink.map(makeCrossLinks));
}

// Temporary hack to deal with the fact that x and y are reversed and it confuses the x events
// Same as in tree, except the x/y accessors are flipped
export const buildLinePathStraightForce = d => `
        M${d.target.x},${d.target.y}
        L${d.source.x},${d.source.y}
      `;
export const buildCrosslinkForce = pair => {
  const [start, end] = pair;
  if (pair.some(item => item === undefined)) {
    return ''
  }
  return `
        M${start.x},${start.y}
        L${end.x},${end.y}
      `;
};

export const render = (anchor, hierarchyData, width) => {
  const root = makeTree(hierarchyData, width);
  const svg = select(anchor);


  svg.select('.treeViz').remove(); // prevent overdrawing
  const nodeList = root.descendants().reverse();
  const g = svg
    .append("g")
    .classed('treeViz', true);

  const yOffset = root.dy / 3;
  const buildLinePath = getBuildLinePath(yOffset);
  const crossLinks = getCrossLinks(nodeList);

  const link = g
    .append("g")
    .classed('linkGroup', true)
    .selectAll("path")
    .data(root.links());
  const crossLink = g
    .append("g")
    .classed('crossLinkGroup', true)
    .selectAll("path")
    .data(crossLinks);

  const nodeGroup = g
    .append("g")
    .classed("nodeGroup", true)
    .selectAll("g")
    .data(nodeList);

  // Bind data to the DOM
  const linkPaths = link.enter()
    .append("path")
    .attr("d", buildLinePath);

  const crossLinkPaths = crossLink.enter()
    .append("path")
    .attr("d", buildCrosslink);

  const node = nodeGroup.enter()
    .append("g")
    .classed("clickableNode", d => d.data.url !== undefined)
    .attr("transform", d => `translate(${d.y},${d.x})`)
    .on("click", d => {
      if (d.data.url) {
        window.open(`${d.data.url}`, "_blank");
      }
    });

  // Tick handlers
  node
    .append("circle")
    .attr("r", 3)
    .attr("fill", d => {
      if (d.data.url) {
        return "#669EFF";
      }
      return d.children ? "#555" : "#999";
    });


  node
    .append("text")
    .attr("dy", "0.31em")
    .attr("x", d => 6) // leaf nodes go to the right.
    .text(d => d.data.name)
    .filter(d => d.children)
    .attr("text-anchor", d => 'start');

  // start animating - might work in react too, just the ticked function is different.
  // Would start the simulation right away at the beginning and use react spring
  // it's really just dragging that breaks down

    const linksList = root.links();
    const crossLinksList = crossLinks.map(([source, target], i) => ({
      source,
      target,
      index: i + linksList.length
    }));

    const allLinks = linksList.concat(crossLinksList).filter(
      link => !isUndefined(link.source) && !isUndefined(link.target)
    );

    // const centerForce = forceCenter()
    //   .x(width / 2 - 90) // for the x translation
    //   .y(0);

    const radialForce = forceRadial()
      .x(width / 2 - 90) // for the x translation
      .y(0)
      .radius(d => d.depth * 40 + 1)
      .strength(0.9);

    const collideForce = forceCollide().radius(20).strength(0.9);
    const chargeForce = forceManyBody().strength(-180);

    const simulation = forceSimulation(nodeList)
      .force("charge", chargeForce)
      .force("radial", radialForce)
      .force("collide", collideForce)
      // .force("center", centerForce) // replace with radial force
      .force("link", forceLink(allLinks).distance(70))
      .stop();

    const ticked = () => {
      const k = 20 * simulation.alpha();
      node.attr("transform", d => `translate(${d.x},${d.y})`);
      // Apply pressure that pushes sources to top, pushes targets to bottom
      linkPaths
        .each(d => {
          d.source.y -= k;
          d.target.y += k;
        })
        .attr("d", buildLinePathStraightForce);
      crossLinkPaths
        .attr("d", buildCrosslinkForce);
    };

    // The fx/fy nodes are for fixing the node to particular positions.
    // https://github.com/d3/d3-force#simulation_nodes
    const dragstarted = (d) => {
      if (!event.active) simulation.alphaTarget(0.2).restart();
      d.fx = d.x;
      d.fy = d.y;
    }
    // Fix the position of the node that we are looking at
    const dragged = (d) => {
      d.fx = event.x;
      d.fy = event.y;
    }
    const dragended = (d) => {
      if (!event.active) simulation.alphaTarget(0); // stop fixing it at the end
      d.fx = null;
      d.fy = null;
    }

    const dragBehavior = drag()
      .on("start", dragstarted)
      .on("drag", dragged)
      .on("end", dragended);

    node.call(dragBehavior);

    simulation
      .on("tick", ticked)
      .restart()
      .tick(150);
}

export default {
  render
}
